
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check,
  RefreshCw,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { auth } from '@/lib/firebase';
import { applyActionCode, AuthError } from 'firebase/auth';
import GlassCard from '@/components/ui/GlassCard';
import AnimatedPage from '@/components/layout/AnimatedPage';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const oobCodeParam = searchParams.get('oobCode');

    if (!oobCodeParam) {
      setError('Brak wymaganego parametru. Link jest nieprawidłowy.');
      setIsVerifying(false);
      return;
    }

    verifyEmail(oobCodeParam);
  }, [searchParams]);

  const verifyEmail = async (code: string) => {
    if (!auth) {
      setError('Firebase Auth nie jest zainicjowany.');
      setIsVerifying(false);
      return;
    }
    try {
      console.log('Weryfikacja maila z kodem:', code?.substring(0, 10) + '...');
      await applyActionCode(auth, code);
      console.log('Email zweryfikowany pomyślnie');

      // Now, call the backend to update the emailVerified status in the other project
      try {
        const response = await fetch('/api/auth/verify-email-backend', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ oobCode: code }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Backend verification failed');
        }

        setSuccess(true);
      } catch (backendError) {
        console.error('Błąd weryfikacji backendu:', backendError);
        setError('Wystąpił błąd podczas komunikacji z serwerem. Spróbuj ponownie.');
      }

      setIsVerifying(false);
    } catch (err) {
      console.error('Błąd weryfikacji maila:', err);
      const authError = err as AuthError;
      handleFirebaseError(authError);
      setIsVerifying(false);
    }
  };

  const handleFirebaseError = (error: AuthError) => {
    console.error('Firebase Error Details:', {
      code: error.code,
      message: error.message,
      customData: error.customData,
      name: error.name
    });

    switch (error.code) {
      case 'auth/expired-action-code':
        setError('Link weryfikacyjny wygasł. Proszę poprosić o nowy link.');
        break;
      case 'auth/invalid-action-code':
        setError('Link weryfikacyjny jest nieprawidłowy lub został już użyty.');
        break;
      case 'auth/user-disabled':
        setError('Konto użytkownika zostało zablokowane. Skontaktuj się z obsługą.');
        break;
      case 'auth/user-not-found':
        setError('Konto użytkownika nie zostało znalezione.');
        break;
      default:
        setError('Wystąpił błąd podczas weryfikacji e-maila: ' + error.message);
    }
  };

  const floatingShapes = [
    { size: 256, x: '10%', y: '20%', duration: 18, blur: 60, color: 'bg-blue-500/10' },
    { size: 192, x: '70%', y: '60%', duration: 22, blur: 40, color: 'bg-cyan-500/10' },
    { size: 320, x: '40%', y: '80%', duration: 16, blur: 80, color: 'bg-indigo-500/5' },
    { size: 128, x: '80%', y: '10%', duration: 20, blur: 30, color: 'bg-blue-400/10' },
  ];

  if (isVerifying) {
    return (
      <AnimatedPage>
        <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${shape.color} blur-3xl`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: shape.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          <GlassCard className="max-w-md w-full text-center p-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="flex justify-center mb-4"
            >
              <RefreshCw className="w-12 h-12 text-blue-400" />
            </motion.div>
            <p className="text-text-primary text-lg">Weryfikowanie adresu e-mail...</p>
          </GlassCard>
        </div>
      </AnimatedPage>
    );
  }

  if (error) {
    return (
      <AnimatedPage>
        <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${shape.color} blur-3xl`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: shape.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          <GlassCard className="max-w-md w-full text-center p-8">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">Błąd weryfikacji</h2>
            <p className="text-text-muted mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25"
            >
              Powrót do strony głównej
            </button>
          </GlassCard>
        </div>
      </AnimatedPage>
    );
  }

  if (success) {
    return (
      <AnimatedPage>
        <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${shape.color} blur-3xl`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: shape.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          <GlassCard className="max-w-md w-full text-center p-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">E-mail zweryfikowany!</h2>
            <p className="text-text-muted mb-6">Twój adres e-mail został pomyślnie zweryfikowany. Możesz teraz zalogować się na swoje konto.</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
            >
              Przejdź do logowania
              <ArrowRight className="w-5 h-5" />
            </button>
          </GlassCard>
        </div>
      </AnimatedPage>
    );
  }

  return null;
};

export default EmailVerification;
